import React from 'react';
import { InventoryActivityReportViewVO } from "../../../types/model/valueObject/InventoryActivityReportViewVO";
import { validateContainerNumber } from '../../../util/ContainerValidator';
import { adminInterchangesReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { formatDriverName } from '../../../util/ReportUtils';
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardItemContainer,
  CollapsableCardLabel,
  CollapsableCardValue,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuItemLabel,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem
} from "../../cardComponents";
import { useMenu } from "../../../hooks/report/useMenu";

type AdminInterchangesCardListItemProps = {
  item: InventoryActivityReportViewVO;
  expanded?: boolean;
  onToggleExpand: () => void;
}

const formatEnumToLabel = (input:string) => input.replace('_', ' ');

/**
 * Component that displays all information for a single admin interchange / inventory activity
 * entry in the Admin Interchanges report.
 * @param item the InventoryActivityReportViewVO to display
 * @param expanded whether the card is expanded or not
 * @param onToggleExpand callback to toggle the expanded state of the card
 * @constructor
 */
const AdminInterchangesCardListItem = ({ item, expanded, onToggleExpand }: AdminInterchangesCardListItemProps) => {

  //hook to handle actions menu state
  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();

  const handleDownloadHistory = () => {
    handleClose();
    window.open(reportEndpoints.actions.history(item.id), '_blank', 'noopener,noreferrer');
  }

  return (
    <CollapsableCardContainer expanded={expanded}>
      <CollapsableCardClickableContainer onClick={onToggleExpand}>
        <CollapsableCardHeaderContainer>
        <CollapsableCardHeaderItem label={'Location'} value={item.locationName} />
        <CollapsableCardHeaderItem label={'Supplier'} value={item.supplierName} />
        <CollapsableCardHeaderItem label={'Buyer'} value={item.buyerName} />
        <CollapsableCardHeaderItem label={'Activity'} value={formatEnumToLabel(item.activity)} />
        <CollapsableCardHeaderItem label={'Date'} value={item.activityDate} />
        <CollapsableCardHeaderItem label={'Booking'} value={item.orderNumber} />
          <CollapsableCardHeaderActionsContainer>
            <CollapsableCardExpandIconButton expanded={expanded ?? false} />
          </CollapsableCardHeaderActionsContainer>
        </CollapsableCardHeaderContainer>
      </CollapsableCardClickableContainer>
      <CollapsableCardCollapseContainer in={expanded}>
        <CollapsableCardBodyContainer>
          <CollapsableCardBodyItem label={'Container'} value={item.containerNumber} />
          <CollapsableCardItemContainer>
            <CollapsableCardLabel>Container Validation</CollapsableCardLabel>
            <CollapsableCardValue>
              {validateContainerNumber(item.containerNumber) ? 'Valid' : 'Invalid'}
            </CollapsableCardValue>
          </CollapsableCardItemContainer>
          <CollapsableCardBodyItem label={'Truck License Plate'} value={item.truckLicensePlateNumber} />
          <CollapsableCardBodyItem label={'Trailer'} value={item.trailerNumber} />
          <CollapsableCardBodyItem label={'Chassis'} value={item.chassisNumber} />
          <CollapsableCardBodyItem label={'Chassis License #'} value={item.chassisLicensePlateNumber} />
          <CollapsableCardBodyItem label={'Seal'} value={item.sealNumber} />
          <CollapsableCardBodyItem label={'Size'} value={item.assetSize} />
          <CollapsableCardBodyItem label={'Type'} value={item.assetType} />
          <CollapsableCardBodyItem label={'Inventory'} value={item.currentInventory} />
          <CollapsableCardBodyItem label={'Dwell Time'} value={item.dwellTimeDisplay} />
          <CollapsableCardBodyItem label={'Dwell Time Minutes'} value={item.dwellTimeInMinutes} />
          <CollapsableCardBodyItem label={'Driver Name'} value={formatDriverName(item.driverFirstName, item.driverLastName)} />
          <CollapsableCardBodyItem label={'License'} value={item.driverLicenseNumber} />
          <CollapsableCardBodyItem label={'Last Updated On'} value={item.lastModifiedDate} />
          <CollapsableCardBodyItem label={'Last Updated By'} value={item.lastModifiedBy} />
          <CollapsableCardBodyItem label={'SCAC Code'} value={item.scacCode} />
          <CollapsableCardBodyItem label={'Notes'} value={item.notes} />
          <CollapsableCardBodyActionsContainer>
            <ActionsMenuOpenButton onClick={handleClick}/>
            <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
              <ActionsMenuItem onClick={handleDownloadHistory} disabled={false}>
                <ActionsMenuItemLabel>Download History</ActionsMenuItemLabel>
              </ActionsMenuItem>
            </ActionsMenu>
          </CollapsableCardBodyActionsContainer>
        </CollapsableCardBodyContainer>
      </CollapsableCardCollapseContainer>
    </CollapsableCardContainer>
  );
};

export { AdminInterchangesCardListItem };
